// Chakra imports
import {
  Box,
  Flex,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  useColorModeValue,
  IconButton,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { API_SERVER } from "config/constant";

// Custom components
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

function OrdersPicked() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  let totalPages = 100; // valor por defecto, se actualizará con la respuesta

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue(
    "gray.200",
    "rgba(255, 255, 255, 0.18)"
  );

  useEffect(() => {
    getOrders();
  }, [page]);

  const getOrders = async () => {
    setLoading(true);
    await axios
      .get(`${API_SERVER}orders/get_picked_list?page=` + page)
      .then(({ data }) => {
        if (data && data.data) {
          setOrders(data.data);
          totalPages = data.last_page || 100;
        } else {
          console.error("API response format unexpected:", data);
          setError("Formato de datos inesperado");
        }
      })
      .catch((err) => {
        console.error("Error fetching picked orders:", err);
        setError("Error al cargar los pedidos en picking");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Filtrar pedidos basados en el término de búsqueda
  const filteredOrders = orders.filter((order) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (order.code && order.code.toLowerCase().includes(searchLower)) ||
      (order.name && order.name.toLowerCase().includes(searchLower)) ||
      (order.order_status &&
        order.order_status.toLowerCase().includes(searchLower)) ||
      (order.reference &&
        order.reference.toLowerCase().includes(searchLower)) ||
      (order.total && order.total.toString().includes(searchLower)) ||
      (order.carrier && order.carrier.toLowerCase().includes(searchLower))
    );
  });

  const next = async () => {
    if (page < totalPages) {
      setPage(page + 1);
      getOrders();
    }
  };

  const back = async () => {
    if (page > 1) {
      setPage(page - 1);
      getOrders();
    }
  };

  const getRowUrl = (order) => {
    return `/#/admin/pedidos/${order.code}`;
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card
        overflowX={{ sm: "scroll", xl: "hidden" }}
        shadow="lg"
        borderRadius="lg"
      >
        <CardHeader p="6px 0px 22px 0px">
          <Flex
            justify="space-between"
            align={{ base: "start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={{ base: "4", md: "0" }}
          >
            <Text
              fontSize={{ base: "lg", md: "xl", xl: "2xl" }}
              color={textColor}
              fontWeight="bold"
            >
              Pedidos en Picking
            </Text>
            <InputGroup
              maxW={{ base: "100%", md: "300px", lg: "400px" }}
              w={{ base: "100%", md: "auto" }}
            >
              <InputLeftElement
                children={<SearchIcon color="gray.500" />}
                pointerEvents="none"
              />
              <Input
                placeholder="Buscar por código, cliente, estado, total..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg={useColorModeValue("white", "gray.700")}
                borderColor={useColorModeValue("gray.200", "gray.600")}
                _focus={{
                  borderColor: "blue.400",
                  boxShadow: "0 0 0 1px blue.400",
                }}
                borderRadius="lg"
              />
            </InputGroup>
          </Flex>
        </CardHeader>

        <CardBody>
          {loading ? (
            <Flex justify="center" align="center" py="50px">
              <Text color={textColor}>Cargando pedidos en picking...</Text>
            </Flex>
          ) : error ? (
            <Flex justify="center" align="center" py="50px">
              <Text color="red.500">{error}</Text>
            </Flex>
          ) : (
            <Table variant="simple" color={textColor}>
              <Thead>
                <Tr>
                  <Th color="gray.400">Orden</Th>
                  <Th color="gray.400">Cliente</Th>
                  <Th color="gray.400">Total</Th>
                  <Th color="gray.400">Estado</Th>
                  <Th color="gray.400">Transportista</Th>
                  <Th color="gray.400">Fecha y Hora</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredOrders.map((row, key) => (
                  <Tr
                    key={key}
                    bg={
                      row.picking_status === 1
                        ? useColorModeValue("yellow.50", "yellow.900")
                        : "transparent"
                    }
                    transition="all 0.2s ease"
                    _hover={{
                      bg:
                        row.picking_status === 1
                          ? useColorModeValue("yellow.100", "yellow.800")
                          : useColorModeValue("gray.50", "gray.700"),
                      cursor: "pointer",
                    }}
                    onClick={() => (window.location.href = getRowUrl(row))}
                  >
                    <Td>
                      <Flex align="center" gap="2">
                        <Text
                          fontSize={{ base: "sm", md: "md" }}
                          color={textColor}
                          fontWeight="bold"
                        >
                          {row.code}
                        </Text>
                        {row.picking_status === 1 && (
                          <Badge
                            bg="yellow.400"
                            color="black"
                            fontSize="10px"
                            p="1px 4px"
                            borderRadius="4px"
                            fontWeight="bold"
                          >
                            PICKING
                          </Badge>
                        )}
                      </Flex>
                    </Td>

                    <Td>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        color={textColor}
                        fontWeight="bold"
                      >
                        {row.name}
                      </Text>
                    </Td>

                    <Td>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        color={textColor}
                        fontWeight="bold"
                      >
                        $
                        {row.total
                          ? row.total.toLocaleString("es-CL", {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })
                          : "0"}
                      </Text>
                    </Td>

                    <Td>
                      <Badge
                        bg={
                          row.order_status === "9.1_Entregado a cliente"
                            ? "green.400"
                            : row.order_status === "8.2_Entregado en destino"
                            ? "blue.400"
                            : row.order_status === "7.5_Out for delivery"
                            ? "orange.400"
                            : row.order_status === "6.0_Salida de bodega"
                            ? "purple.400"
                            : row.order_status === "5.0_Picking en proceso"
                            ? "yellow.400"
                            : row.order_status === "4.1_Pago verificado"
                            ? "cyan.400"
                            : row.order_status === "3.0_Pago pendiente"
                            ? "red.400"
                            : row.order_status === "2.0_Cancelado"
                            ? "gray.400"
                            : "gray.400"
                        }
                        color="white"
                        fontSize="sm"
                        p="6px 10px"
                        borderRadius="md"
                      >
                        {row.order_status || "Sin estado"}
                      </Badge>
                    </Td>

                    <Td>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        color={textColor}
                        fontWeight="bold"
                      >
                        {row.carrier || "N/A"}
                      </Text>
                    </Td>

                    <Td>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        color={textColor}
                        fontWeight="bold"
                      >
                        {row.date_add || row.updated_at || "N/A"}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Pagination */}
      {orders.length > 0 && (
        <Flex
          justify="space-between"
          align="center"
          mt="20px"
          direction={{ base: "column", md: "row" }}
          gap={{ base: "4", md: "0" }}
        >
          <Text fontSize="sm" color="gray.500">
            Página {page} de {totalPages}
          </Text>
          <Flex gap="2">
            <Button
              onClick={back}
              isDisabled={page <= 1}
              bg={useColorModeValue("gray.200", "gray.700")}
              color={textColor}
              _hover={{
                bg: useColorModeValue("gray.300", "gray.600"),
              }}
              _disabled={{
                bg: useColorModeValue("gray.100", "gray.800"),
                color: "gray.400",
                cursor: "not-allowed",
              }}
            >
              Anterior
            </Button>
            <Button
              onClick={next}
              isDisabled={page >= totalPages}
              bg={useColorModeValue("blue.500", "blue.600")}
              color="white"
              _hover={{
                bg: useColorModeValue("blue.600", "blue.700"),
              }}
              _disabled={{
                bg: useColorModeValue("gray.100", "gray.800"),
                color: "gray.400",
                cursor: "not-allowed",
              }}
            >
              Siguiente
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}

export default OrdersPicked;
